import React, { useState, useEffect, useRef, useContext } from "react";
import { useDispatch } from "react-redux";
import { Button } from "reactstrap";
import RecordRTC, { StereoAudioRecorder } from "recordrtc";
import * as BackendHelper from "../../../../../../helpers/backend_helper";
import { toast } from "react-toastify";
import { InterviewContext } from "../../../context/InterviewProvider";
import { attemptFailPreAssessment } from "@workspace/job/src/store/preAssessment/action";
import { interviewConductStatus } from "../interviewVideoChatConstant";

// Import styles
import "./InterviewVideoAI.scss";

const defaultInterviewQuestions = `
1) How old are you?
2) What is your favorite movie?
`;

const InterviewVideoAIPlatformRealtime = ({
  token = null,
  onCompleteInterview,
  setOffVideoCanvasOpen,
  interviewQuestions,
  setStep,
  setIsInterviewAttemptFailedModalOpen,
  language = {
    label: "English",
    value: "english",
  },
}) => {
  // ================== Settings ==================
  const audioPlaybackRate = 1; // Audio playback rate
  // =================================================
  // ================== Feature Flags ==================
  const ALLOW_INTERUPTION = false; // Set to false to enable turn detection(allow interuption)
  // ================================================

  const dispatch = useDispatch();
  const interviewContext = useContext(InterviewContext);
  const [sessionId, setSessionId] = useState(null);
  const [isDone, setIsDone] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const analyserIntervalRef = useRef(null);
  const isRecordingRef = useRef(false);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [videoVolume, setVideoVolume] = useState(1); // Default full volume

  const [isSessionActive, setIsSessionActive] = useState(false);
  const [events, setEvents] = useState([]);
  const [dataChannel, setDataChannel] = useState(null);
  const peerConnection = useRef(null);
  const audioElement = useRef(null);
  const botAudioStreamRef = useRef(null); // Persistent bot audio stream
  const [aiHistory, setAiHistory] = useState([]);
  const [humanHistory, setHumanHistory] = useState([]);
  const [combinedHistory, setCombinedHistory] = useState([]);
  const [isLastAudioCompleted, setIsLastAudioCompleted] = useState(false);
  const transcriptOrderRef = useRef(0);

  const [isRecordingBot, setIsRecordingBot] = useState(false);
  const botRecordingRef = useRef(null);
  const botAudioChunksRef = useRef([]);
  const audioStartTimeRef = useRef(false);
  const capturedStartTimeRef = useRef(null);
  const userAudioTrackRef = useRef(null);

  const questions = `
  1. How old are you?
  2. What is your favourite movie?
            `;

  // =================== RealTime ===================
  function monitorBotAudio(botStream, startRecordingCallback) {
    const audioContext = new AudioContext();
    const botAudioSource = audioContext.createMediaStreamSource(botStream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    botAudioSource.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const SILENCE_THRESHOLD = 5; // Amplitude below this is considered silence
    const SILENCE_DURATION = 1000; // Duration (in ms) to confirm silence

    let silenceStart = null;
    let silenceInterval;

    const monitorSpeech = () => {
      analyser.getByteTimeDomainData(dataArray);
      const amplitude = Math.max(...dataArray.map((v) => Math.abs(v - 128)));

      if (amplitude < SILENCE_THRESHOLD) {
        if (!silenceStart) silenceStart = Date.now();
        if (Date.now() - silenceStart >= SILENCE_DURATION) {
          startRecordingCallback(); // Trigger recording
          clearInterval(silenceInterval); // Stop monitoring
        }
      } else {
        silenceStart = null; // Reset silence timer if sound is detected
      }
    };

    silenceInterval = setInterval(monitorSpeech, 100); // Check every 100ms

    return () => clearInterval(silenceInterval); // Cleanup function
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]); // Extract only the Base64 part
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob); // Read the blob as a Data URL
    });
  }

  const startBotRecording = (stream) => {
    try {
      // Initialize MediaRecorder for bot audio
      botRecordingRef.current = new MediaRecorder(stream);
      botAudioChunksRef.current = [];
      setIsRecordingBot(true);

      // Handle data available event
      botRecordingRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          if (!audioStartTimeRef.current) {
            capturedStartTimeRef.current =
              interviewContext.getElapsedTime() - 1000;
            audioStartTimeRef.current = true;
          }
          botAudioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop event
      botRecordingRef.current.onstop = () => {
        const audioBlob = new Blob(botAudioChunksRef.current, {
          type: "audio/webm",
        });

        blobToBase64(audioBlob).then((base64data) => {
          interviewContext.addBotTimestamp(
            null,
            base64data,
            null,
            "bot_audio",
            capturedStartTimeRef.current
          );
        });
      };

      // Start recording with appropriate timeslice (e.g., 1000ms chunks)
      botRecordingRef.current.start(1000);
    } catch (error) {
      console.error("Error starting bot recording:", error);
    }
  };

  // Start the interview session
  async function startSession() {
    // Get an ephemeral key from the Fastify server
    interviewContext.setBotStatus(interviewConductStatus?.STARTING_INTERVIEW);

    const tokenResponse = await BackendHelper.getToken({
      questions: interviewQuestions || questions,
      language: language?.value || "english",
    });

    const data = tokenResponse;
    const EPHEMERAL_KEY = data.client_secret.value;

    // Create a peer connection
    const pc = new RTCPeerConnection();

    // Set up to play remote audio from the model
    audioElement.current = document.createElement("audio");
    audioElement.current.autoplay = true;

    pc.ontrack = (e) => {
      const [remoteStream] = e.streams;
      botAudioStreamRef.current = remoteStream; // Store bot's audio stream
      audioElement.current.srcObject = remoteStream; // Play bot audio

      startBotRecording(remoteStream);
    };

    // Add local audio track for microphone input in the browser
    const ms = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    userAudioTrackRef.current = ms.getAudioTracks()[0];

    // Disable the microphone initially
    userAudioTrackRef.current.enabled = false;

    pc.addTrack(ms.getTracks()[0]);

    // Set up data channel for sending and receiving events
    const dc = pc.createDataChannel("oai-events");
    setDataChannel(dc);

    // Start the session using the Session Description Protocol (SDP)
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const baseUrl = "https://api.openai.com/v1/realtime";
    const model = "gpt-4o-mini-realtime-preview";
    const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${EPHEMERAL_KEY}`,
        "Content-Type": "application/sdp",
      },
    });

    const answer = {
      type: "answer",
      sdp: await sdpResponse.text(),
    };
    await pc.setRemoteDescription(answer);

    peerConnection.current = pc;
  }

  // Stop the interview session
  function stopSession() {
    if (botRecordingRef.current && isRecordingBot) {
      botRecordingRef.current.stop();
      setIsRecordingBot(false);
    }

    if (dataChannel) {
      dataChannel.close();
    }
    if (peerConnection.current) {
      peerConnection.current.close();
    }

    setIsSessionActive(false);
    setDataChannel(null);
    peerConnection.current = null;
  }

  // Send a message to the model
  function sendClientEvent(message) {
    if (dataChannel) {
      message.event_id = message.event_id || crypto.randomUUID();
      dataChannel.send(JSON.stringify(message));
      setEvents((prev) => [message, ...prev]);
    } else {
      console.error(
        "Failed to send message - no data channel available",
        message
      );
    }
  }

  // Send a text message to the model
  function sendTextMessage(message) {
    const event = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          {
            type: "input_text",
            text: message,
          },
        ],
      },
    };

    sendClientEvent(event);
    sendClientEvent({ type: "response.create" });
  }

  // Attach event listeners to the data channel when a new one is created
  useEffect(() => {
    let cleanupSilenceDetection;

    if (dataChannel) {
      // Append new server events to the list
      dataChannel.addEventListener("message", (e) => {
        // Parse the incoming message and save all events for logging
        const event = JSON.parse(e.data);
        setEvents((prev) => [JSON.parse(e.data), ...prev]);

        // Event (response.done)
        if (event.type === "response.done") {
          const transcript =
            event?.response?.output?.[0]?.content?.[0]?.transcript;
          if (transcript && transcript.includes("<-//->")) {
            if (botAudioStreamRef.current) {
              cleanupSilenceDetection = monitorBotAudio(
                botAudioStreamRef.current,
                () => {
                  setIsLastAudioCompleted(true);
                }
              );
            } else {
              console.error("Bot audio stream is not initialized.");
            }
            transcriptOrderRef.current = transcriptOrderRef.current + 1;
            setAiHistory((prev) => {
              if (event?.response?.output?.[0]?.content?.[0]?.transcript) {
                const aiResponse = {
                  type: "ai",
                  content:
                    event?.response?.output?.[0]?.content?.[0]?.transcript,
                  order: transcriptOrderRef.current,
                };
                return [...prev, aiResponse];
              }
              return prev;
            });
          } else {
            if (botAudioStreamRef.current) {
              cleanupSilenceDetection = monitorBotAudio(
                botAudioStreamRef.current,
                startListening
              );
            } else {
              console.error("Bot audio stream is not initialized.");
            }
            transcriptOrderRef.current = transcriptOrderRef.current + 1;
            setAiHistory((prev) => {
              if (event?.response?.output?.[0]?.content?.[0]?.transcript) {
                const aiResponse = {
                  type: "ai",
                  content:
                    event?.response?.output?.[0]?.content?.[0]?.transcript,
                  order: transcriptOrderRef.current,
                };
                return [...prev, aiResponse];
              }
              return prev;
            });
          }
        }

        // Event (response.create)
        if (event.type === "response.create") {
          cleanupSilenceDetection?.();
        }

        // Event (input_audio_buffer.speech_stopped)
        if (event.type === "input_audio_buffer.speech_stopped") {
          stopAudioCapture();
          clearInterval(analyserIntervalRef.current);
        }

        // Event (conversation.item.created)
        if (event.type === "conversation.item.created") {
          const content = event?.item?.content[0];
          if (content && content.type === "input_text") {
            const humanResponse = {
              type: "human",
              content: content?.text,
              order: transcriptOrderRef.current,
            };
            setHumanHistory((prev) => [...prev, humanResponse]);
          }
        }

        // Event (response.content_part.added)
        if (event.type === "response.content_part.added") {
          interviewContext.setBotStatus(interviewConductStatus.BOT_SPEAKING);
        }
      });

      // Set session active when the data channel is opened
      // Update the session with some parameters
      dataChannel.addEventListener("open", () => {
        setIsSessionActive(true);
        setEvents([]);
        const updateSession = {
          type: "session.update",
          session: {
            turn_detection: {
              type: "server_vad",
              silence_duration_ms: 2000,
            },
            input_audio_transcription: null,
          },
        };
        sendClientEvent(updateSession);
        sendTextMessage("Please start the interview.");
      });
    }
  }, [dataChannel]);

  useEffect(() => {
    startSession().catch((error) => {
      console.error("Error in startSession:", error);
      toast.error("Error in startSession:", error);
      stopSession();
      interviewContext?.abortRequest();
      interviewContext.setInterviewStatus("return");
    });
  }, []);

  // =================== RealTime ===================

  const toggleMicrophone = async (enable) => {
    if (!userAudioTrackRef.current) {
      console.error("User audio track is not initialized.");
      return;
    }

    if (enable) {
      if (userAudioTrackRef.current.enabled) {
        return;
      }
      userAudioTrackRef.current.enabled = true; // Enable user microphone track
    } else {
      if (!userAudioTrackRef.current.enabled) {
        return;
      }
      userAudioTrackRef.current.enabled = false; // Disable user microphone track
    }
  };

  // Disable microphone during bot speaking
  useEffect(() => {
    if (ALLOW_INTERUPTION || !userAudioTrackRef.current) {
      return;
    }

    if (
      interviewContext?.botStatus ===
      interviewConductStatus.RECORDING_INTERVIEWEE_RESPONSE
    ) {
      toggleMicrophone(true);
    } else {
      toggleMicrophone(false);
    }
  }, [interviewContext?.botStatus]);

  useEffect(() => {
    if (isLastAudioCompleted) {
      const delay = setTimeout(() => {
        stopSession();
        setIsDone(true);
      }, 2500);
      return () => clearTimeout(delay);
    }
  }, [isLastAudioCompleted, humanHistory, aiHistory]);

  // Combine human and AI messages in sequential order
  useEffect(() => {
    const userHistoryCopy = [...humanHistory]; // Copy user messages
    const botHistoryCopy = [...aiHistory]; // Copy bot messages
    const updatedCombinedHistory = [];

    // Step 1: Add the special first human message (order = 0)
    if (userHistoryCopy.length > 0 && userHistoryCopy[0].order === 0) {
      updatedCombinedHistory.push(userHistoryCopy.shift()); // Add and remove the first human message
    }

    // Step 2: Process and alternate AI and human messages in sequential order
    let currentOrder = 1;

    while (userHistoryCopy.length > 0 || botHistoryCopy.length > 0) {
      // Add AI messages for the current order
      while (
        botHistoryCopy.length > 0 &&
        botHistoryCopy[0].order === currentOrder
      ) {
        updatedCombinedHistory.push(botHistoryCopy.shift());
      }

      // Add Human messages for the current order
      while (
        userHistoryCopy.length > 0 &&
        userHistoryCopy[0].order === currentOrder
      ) {
        updatedCombinedHistory.push(userHistoryCopy.shift());
      }

      // Increment the current order for the next round
      currentOrder++;
    }

    // Step 3: Update state with the combined history
    setCombinedHistory(updatedCombinedHistory);
    interviewContext.setChatHistory(updatedCombinedHistory);
  }, [humanHistory, aiHistory]);

  const startListening = () => {
    navigator.mediaDevices
      .getUserMedia({
        audio: {
          autoGainControl: true, // Automatically adjust gain
          noiseSuppression: true, // Reduce background noise
          echoCancellation: false, // Disable echo cancellation if not needed
        },
      })
      .then((stream) => {
        const audioContext = new AudioContext();
        const audioSource = audioContext.createMediaStreamSource(stream);

        // Added audio filter to remove high-frequency noise
        const biquadFilter = audioContext.createBiquadFilter();
        biquadFilter.type = "lowpass"; // Low-pass filter to remove high-frequency noise
        biquadFilter.frequency.value = 3000; // Cutoff frequency in Hz (adjust as needed)
        biquadFilter.Q.value = 1; // Quality factor (adjust for sharpness of the cutoff)

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;

        // Old
        // audioSource.connect(analyser);

        // Connect the nodes
        audioSource.connect(biquadFilter); // Audio source -> Filter
        biquadFilter.connect(analyser); // Filter -> Analyser

        // ================== Audio processing & Recording settings ================
        let silenceBuffer = 0; // Buffer for silence detection
        const SILENCE_AMPLITUDE_THRESHOLD = 8; // Amplitude threshold
        const MAX_SILENCE_BUFFER = 10; // Allow up to 5 intervals of silence before stopping
        const CHECK_INTERVAL = 200; // Check every 200ms (adjust for stability)
        // =========================================================================

        // Flag to check if recording has started
        let hasRecordingStarted = false;
        setBotStatus(interviewConductStatus?.WAITING_FOR_INTERVIEWEE_RESPONSE);
        interviewContext.setBotStatus(
          interviewConductStatus?.WAITING_FOR_INTERVIEWEE_RESPONSE
        );
        interviewContext.addBotTimestamp(
          null,
          null,
          interviewConductStatus?.WAITING_FOR_INTERVIEWEE_RESPONSE
        );

        // Start monitoring for sound above threshold
        analyserIntervalRef.current = setInterval(() => {
          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteTimeDomainData(dataArray); // Get waveform data

          // Calculate the max amplitude
          const maxAmplitude = Math.max(
            ...dataArray.map((value) => Math.abs(value - 128))
          );

          if (!hasRecordingStarted) {
            // Start recording when amplitude exceeds the threshold
            recorderRef.current = RecordRTC(stream, {
              type: "audio",
              mimeType: "audio/webm",
              recorderType: StereoAudioRecorder,
              numberOfAudioChannels: 1,
              desiredSampRate: 16000,
              bufferSize: 4096,
              // desiredSampRate: 44100,
              // bufferSize: 8192,
              disableLogs: false,
            });

            recorderRef.current.startRecording();
            isRecordingRef.current = true;
            setIsRecording(true);
            setBotStatus(
              interviewConductStatus?.RECORDING_INTERVIEWEE_RESPONSE
            );
            interviewContext.setBotStatus(
              interviewConductStatus?.RECORDING_INTERVIEWEE_RESPONSE
            );
            interviewContext.addBotTimestamp(
              null,
              null,
              interviewConductStatus?.RECORDING_INTERVIEWEE_RESPONSE
            );
            hasRecordingStarted = true;
          }
        }, CHECK_INTERVAL);
      })
      .catch((error) => console.error("Error accessing microphone:", error));
  };

  const stopAudioCapture = () => {
    if (recorderRef.current && isRecordingRef.current) {
      recorderRef.current.stopRecording(() => {
        const audioBlob = recorderRef.current.getBlob();
        if (audioBlob) {
          setRecordedAudioBlob(audioBlob);
        }
        isRecordingRef.current = false;
        setIsRecording(false);
      });
    }
  };

  useEffect(() => {
    if (recordedAudioBlob) {
      handleAudioSubmission(recordedAudioBlob);
    }
  }, [recordedAudioBlob]);

  const handleAudioSubmission = async (audioBlob) => {
    const fileData = new File([audioBlob], "recording.webm", {
      type: "audio/webm",
    });
    const formData = new FormData();
    formData.append("audio_file", fileData);
    formData.append("language", language?.value);
    formData.append("order", transcriptOrderRef.current);
    const abortController = interviewContext?.createAbortController(); // Create/reset AbortController

    try {
      setBotStatus(interviewConductStatus?.WAITING_FOR_INTERVIEWER_RESPONSE);
      interviewContext.setBotStatus(
        interviewConductStatus?.WAITING_FOR_INTERVIEWER_RESPONSE
      );
      interviewContext.addBotTimestamp(
        null,
        null,
        interviewConductStatus?.WAITING_FOR_INTERVIEWER_RESPONSE
      );
      const response = await BackendHelper.transcribeAudio(formData, {
        signal: abortController.signal,
      });
      const humanResponse = {
        type: "human",
        content: response?.transcript,
        order: response?.order,
      };
      setHumanHistory((prev) => [...prev, humanResponse]);
    } catch (error) {
      setBotStatus(null);
      interviewContext.setBotStatus(null);
      interviewContext.addBotTimestamp(null, null, null);
      if (error === "canceled") {
        toast.info("Interview was cancelled.");
        return;
      }
      toast.error("Error submitting audio. Please try again.");
      console.error("Error submitting audio:", error);
    }
  };

  useEffect(() => {
    if (isDone) {
      interviewContext.setInterviewStatus("end");
    }
  }, [isDone]);

  const clearStates = () => {
    setSessionId(null);
    setChatHistory([]);
    interviewContext.setChatHistory([]);
    setBotStatus(null);
    interviewContext.setBotStatus(null);
  };

  useEffect(() => {
    if (interviewContext.interviewStatus === "return") {
      clearStates();
      toast.info("You have left the interview.");
      setStep(0);
      setIsInterviewAttemptFailedModalOpen(true);
      dispatch(
        attemptFailPreAssessment({
          token,
        })
      );
    }
  }, [interviewContext.interviewStatus]);

  const getVolumeIcon = () => {
    if (videoVolume === 0) {
      return "ri-volume-mute-line";
    } else if (videoVolume < 0.5) {
      return "ri-volume-down-line";
    } else {
      return "ri-volume-up-line";
    }
  };

  const handleVolumeChange = (e) => {
    const volume = parseFloat(e.target.value);
    setVideoVolume(volume);

    // Update the volume of the currently playing audio
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  };

  return (
    <div>
      {/* Video External Controls 1) Range, Play and pause and Volume */}
      <div className="d-flex  justify-content-between mt-2">
        <div className="d-flex align-items-center gap-3">
          <i
            className=" ri-settings-2-line cursor-pointer"
            style={{ fontSize: "1.5rem" }}
          />
          <div className="d-flex gap-2 align-items-center">
            <i
              className={getVolumeIcon()}
              style={{
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
            ></i>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={videoVolume}
              onChange={handleVolumeChange}
              style={{ width: "100%" }}
            />
          </div>
        </div>

        {/* // Blur Button */}
        <div style={{
          display:"none"
        }}>
          <span
            onClick={() =>
              interviewContext.setIsBlurBackgroundEnabled((prev) => !prev)
            }
          >
            <i
              className={`cursor-pointer ${
                interviewContext.isBlurBackgroundEnabled ||
                interviewContext.isBlurBackgroundEnabled == null
                  ? "ri-blur-off-line"
                  : "ri-drop-line"
              }`}
              style={{ fontSize: "1.5rem" }}
              title={
                interviewContext.isBlurBackgroundEnabled ||
                interviewContext.isBlurBackgroundEnabled == null
                  ? "Disable Blur Background"
                  : "Enable Blur Background"
              }
            />
          </span>
        </div>

        <Button
          className="btn btn-danger"
          style={{
            borderRadius: "100vh",
          }}
          onClick={() => {
            interviewContext.setInterviewStatus("stop");
            interviewContext?.abortRequest();
            stopSession();
          }}
        >
          Leave Meeting
        </Button>
      </div>
    </div>
  );
};

export default InterviewVideoAIPlatformRealtime;
